import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Sentry from "@sentry/react";
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface CompleteProfileParams {
  role: 'business_owner' | 'potential_successor' | 'consultant_advisor';
  legal_first_name: string;
  legal_last_name: string;
  company_name?: string;
  business_structure?: string;
  industry?: string;
  goals?: string[];
  has_specific_business?: string;
  successor_relationship?: string;
  advisor_relationship?: string;
  client_count?: string;
}

export interface CompleteProfileResponse {
  success: boolean;
  user_id: string;
  organization_id: string;
  role: string;
  message: string;
}

export function useCompleteProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CompleteProfileParams): Promise<CompleteProfileResponse> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Using direct query since the RPC function might not be in the generated types yet
      const { data, error } = await supabase
        .rpc('complete_user_profile_transaction' as any, {
          p_user_id: user.id,
          p_role: params.role,
          p_legal_first_name: params.legal_first_name,
          p_legal_last_name: params.legal_last_name,
          p_company_name: params.company_name || null,
          p_business_structure: params.business_structure || null,
          p_industry: params.industry || null,
          p_goals: params.goals || [],
          p_has_specific_business: params.has_specific_business || null,
          p_successor_relationship: params.successor_relationship || null,
          p_advisor_relationship: params.advisor_relationship || null,
          p_client_count: params.client_count || null
        });

      if (error) {
        Sentry.captureException(error, {
          tags: { context: 'rpc_call', component: 'useCompleteProfile' },
          extra: { message: 'RPC call error', userId: user?.id, role: params.role }
        });
        throw new Error(error.message || 'Failed to complete profile');
      }

      // Cast the response to our expected type
      const response = data as CompleteProfileResponse;
      
      if (!response || !response.success) {
        throw new Error(response?.message || 'Profile completion failed');
      }

      return response;
    },
    onSuccess: (data) => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['user-role'] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      
      toast({
        title: "Profile completed",
        description: data.message || "Your profile has been set up successfully!"
      });
    },
    onError: (error: Error) => {
      Sentry.captureException(error, {
        tags: { context: 'profile_completion', component: 'useCompleteProfile' },
        extra: { message: 'Profile completion error' }
      });
      
      // Parse common error messages and provide user-friendly feedback
      let errorMessage = error.message;
      let errorTitle = "Profile completion failed";
      
      if (error.message.includes('duplicate key value')) {
        errorTitle = "Organization already exists";
        errorMessage = "An organization with this name already exists. Please choose a different name.";
      } else if (error.message.includes('foreign key constraint')) {
        errorTitle = "Invalid data";
        errorMessage = "Some of the provided information is invalid. Please check your entries.";
      } else if (error.message.includes('not-null constraint')) {
        errorTitle = "Missing required information";
        errorMessage = "Please fill in all required fields.";
      }
      
      toast({
        title: errorTitle,
        description: errorMessage,
        variant: "destructive"
      });
    }
  });
}